/**
 * AuthWrapper.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Thin Cognito Hosted UI authentication guard.
 *
 * Flow:
 *   1. If cognitoEnabled is false (SSM not yet bootstrapped), renders children
 *      immediately — backwards-compatible no-op.
 *   2. On the OAuth callback (?code=...), exchanges the authorization code for
 *      tokens via Cognito's /oauth2/token endpoint and stores id_token +
 *      access_token in sessionStorage, then strips the ?code param from the URL.
 *   3. If a valid id_token already exists in sessionStorage, renders children.
 *   4. Otherwise redirects the browser to the Cognito Hosted UI login page.
 *
 * Token storage: sessionStorage only (cleared on tab close — HIPAA appropriate).
 * No third-party library required — uses the standard fetch API.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect } from "react";
import CognitoConfig, { cognitoEnabled } from "./CognitoConfig";

const TOKEN_KEY    = "gnq_id_token";
const EXPIRES_KEY  = "gnq_token_expires";

/** Returns true if a non-expired id_token exists in sessionStorage. */
function isAuthenticated() {
  const token   = sessionStorage.getItem(TOKEN_KEY);
  const expires = parseInt(sessionStorage.getItem(EXPIRES_KEY) || "0", 10);
  return Boolean(token && Date.now() < expires);
}

/** Parses the JWT payload (no signature validation needed — Cognito validated it). */
function parseJwtPayload(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return {};
  }
}

/** Exchanges an authorization code for tokens via Cognito /oauth2/token. */
async function exchangeCode(code) {
  const body = new URLSearchParams({
    grant_type:   "authorization_code",
    client_id:    CognitoConfig.clientId,
    redirect_uri: CognitoConfig.redirectUri,
    code,
  });

  const res = await fetch(`https://${CognitoConfig.domain}/oauth2/token`, {
    method:  "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body:    body.toString(),
  });

  if (!res.ok) {
    // Do NOT include the raw response body — it may echo back client_id or
    // domain values that should not appear in browser console or error UIs.
    throw new Error(`Cognito token exchange failed (HTTP ${res.status})`);
  }

  return res.json(); // { id_token, access_token, refresh_token, expires_in }
}

export default function AuthWrapper({ children }) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // If Cognito is not configured, pass through immediately.
    if (!cognitoEnabled) {
      setReady(true);
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const code   = params.get("code");

    if (code) {
      // ── OAuth callback: exchange code for tokens ──────────────────────────
      exchangeCode(code)
        .then((tokens) => {
          const payload    = parseJwtPayload(tokens.id_token);
          const expiresAt  = Date.now() + (tokens.expires_in || 3600) * 1000;
          sessionStorage.setItem(TOKEN_KEY,   tokens.id_token);
          sessionStorage.setItem(EXPIRES_KEY, String(expiresAt));

          // Remove ?code from URL without triggering a page reload
          const clean = window.location.pathname + window.location.hash;
          window.history.replaceState({}, document.title, clean);

          // Log identity without leaking the token or sensitive Cognito IDs.
          // Only the sub (opaque UUID) is safe to log; email is PII — omit it.
          const sub = payload.sub ? `${payload.sub.slice(0, 8)}…` : "unknown";
          console.info("[Auth] Session established. sub:", sub);
          setReady(true);
        })
        .catch((err) => {
          // Log only the sanitized message — never the full Error object which
          // may include the request body containing client_id.
          console.error("[Auth] Token exchange failed:", err.message);
          setError(err.message);
        });
      return;
    }

    if (isAuthenticated()) {
      // ── Already have a valid token ────────────────────────────────────────
      setReady(true);
      return;
    }

    // ── No token, no code → redirect to Cognito Hosted UI ────────────────────
    window.location.href = CognitoConfig.loginUrl;
    // setReady stays false — browser is navigating away
  }, []); // eslint-disable-line

  if (error) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "system-ui, sans-serif", color: "#d1242f" }}>
        <div style={{ textAlign: "center", maxWidth: 480 }}>
          <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Authentication Error</p>
          <p style={{ fontSize: 13, color: "#57606a" }}>{error}</p>
          <button
            onClick={() => { sessionStorage.clear(); window.location.href = CognitoConfig.loginUrl; }}
            style={{ marginTop: 16, padding: "8px 20px", background: "#3b82d4", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 14 }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "system-ui, sans-serif", color: "#57606a", fontSize: 14 }}>
        Authenticating…
      </div>
    );
  }

  return children;
}
